import { 
    Box,
    Button, 
    Paper, 
    Typography, 
    LinearProgress, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    IconButton, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogContentText, 
    DialogActions,
    CircularProgress
} from "@mui/material";
import { useEffect, useState} from "react";
import { useNavigate } from "react-router-dom";
import DeleteIcon from '@mui/icons-material/Delete';
import API from '../../api/axiosConfig';
import { alpha } from '@mui/material/styles';
import { useUser } from "../../context/UserContext"; 


export default function CandidateTrackerPage(){

    const navigate = useNavigate();

    // GLOBAL AUTH STATE
    const { userEmail } = useUser();

    //DATA STATE
    // Candidates storage
    const [candidates, setCandidates] = useState(null);
    // Display filter
    const [filter, setFilter] = useState("All");
    // State to manage error status
    const [error, setError] = useState(null);
    // Loading state
    const [loading, setLoading] = useState(true);

    // DELETION STATE
    // Status of deleting candidate (used for dialog)
    const [deleteStatus, setDeleteStatus] = useState('idle'); 
    // Error message for deleting (Used to pass to dialog if error occurs)
    const [deleteError, setDeleteError] = useState(''); 
    // Deletion control variables 
    const [openDialog, setOpenDialog] = useState(false); // Controls if the dialog is open
    const [candidateToDelete, setCandidateToDelete] = useState(null); // Stores the candidate object to be deleted

    // List of boolean fields to track for progress
    const progressFields = [
        "information",
        "resume",
        "position",
        "interview",
        "salary",
        "accepted",
        "clearanceTransferred",
        "startDate",
        "emailGiven",
        "welcome",
        "insurance"
    ];

    useEffect(() => {
       getAllCandidates();
    }, []);

    const getAllCandidates = async () => {
        try {
            const response = await API.post('/api/candidates/all', {});
            const candidates = response.data.candidates;

            if (candidates) {
                const candidatesArray = Object.keys(candidates).map(id => ({
                    id: id + 1, // Assign the key as the 'id' property
                    ...candidates[id] // Spread the rest of the candidate data
                }));

                setCandidates(candidatesArray);
            }
        } catch (error) {
            setError("An error occurred while fetching candidates.");      
        } finally {
            setLoading(false);
        }
    }

    const handleConfirmDelete = async () => {
        if (!candidateToDelete) {
            return;
        }
        setDeleteStatus('deleting');
        try {
            await API.post('/api/candidates/delete', {
                id: candidateToDelete.id
            });

    
            setDeleteStatus('success');
            setCandidates(prev => prev.filter(c => c.id !== candidateToDelete.id));
            //setTimeout(() => handleCloseDialog(), 1500); // Auto close on success
                                    
        } catch (error) {
            setDeleteStatus('error');
            if(error.response.status === 404){ // This is the most likely error
                setDeleteError("Candidate not found!");
            } else { // this could be a npe or catch other unknown server errors
                setDeleteError("Unknown error ocurred " + error.response.status);
            }
        }
    };

    // Function to calculate progress for a single candidate
    const calculateCandidateProgress = (candidate) => {
        let completedSteps = 0;
        progressFields.forEach(field => {
            if (candidate[field]) {
                completedSteps++;
            }
        });
        const totalSteps = progressFields.length;
        return (completedSteps / totalSteps) * 100;
    };

    // Candidate filter
    const filteredCandidates = candidates ? candidates.filter(candidate => {
        if (filter === 'Completed') {
            return candidate.completed;
        } else if (filter === 'In Progress') {
            return !candidate.completed;
        }
        return true; // 'All' selected or no filter
    }) : [];

    // Sort the candidates
    const sortedCandidates = [...filteredCandidates].sort((a, b) => {
        const progressA = calculateCandidateProgress(a);
        const progressB = calculateCandidateProgress(b);
        return progressA - progressB; // Descending order
    });

    // Set the candidate that will be deleted and open dialog
    const handleClickOpenDialog = (candidate) => {
        setCandidateToDelete(candidate); 
        setOpenDialog(true); 
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setTimeout(() => { // timeout prevents user from seeing undefined while box is closing
            setCandidateToDelete(null);  // clear candidate
            setDeleteStatus('idle');    // Reset the status for next time
            setDeleteError('');
        }, 300);
    };

    // Wait for Context to load
    if (userEmail === null) {
        return (
            <Box sx={{ p: 4 }}>
                <CircularProgress size={20} />
                <Typography sx={{ mt: 2 }}>Verifying Credentials...</Typography>
            </Box>
        );
    }



    // Data Fetching state
    if (loading && candidates === null) {
        return <Typography sx={{ p: 4 }}>Loading candidate data...</Typography>;
    }

    return(
        <div>
            <h1>New Hire Candidate Tracker</h1>
            <Button
                variant="contained"
                sx={{bgcolor:"darkred",
                    '&:hover': {
                        bgcolor: 'red',
                    }
                }}
                onClick={() => navigate("/candidates/add")}
            >
                New Candidate
            </Button>
            <Typography
                sx={{
                    margin:"16px"
                }}
                variant="h6"
            >
                Candidate Pipeline
            </Typography>
            <FormControl sx={{ minWidth: 120 }} size="small">
                    <InputLabel id="filter-select-label">Filter</InputLabel>
                    <Select
                        labelId="filter-select-label"
                        id="filter-select"
                        value={filter}
                        label="Filter"
                        onChange={(event) => setFilter(event.target.value)}
                    >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                        <MenuItem value="In Progress">In Progress</MenuItem>
                    </Select>
            </FormControl>

            {error? (
                <Typography>Error: {error} </Typography>
            ) : candidates === null ? (
                <Typography>Loading candidates...</Typography>
            ) : candidates.length === 0 ? (
                <Typography>No candidates found in the pipeline.</Typography>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        margin: '16px auto',
                        maxWidth: 900
                    }}
                >
                    {sortedCandidates.map((candidate) => {
                        // Use candidate.completed directly
                        const isCompleted = candidate.completed; 
                        return (
                            <Paper
                                key={candidate.id}
                                elevation={3}
                                // Make the entire Paper clickable only if not completed
                                onClick={() => !isCompleted && navigate(`/candidates/view/${candidate.id}`)}
                                sx={(theme) => ({
                                    padding: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    cursor: isCompleted ? 'not-allowed' : 'pointer', // Change cursor when disabled
                                    width: '100%',


                                    backgroundColor: isCompleted ? theme.palette.background.paper : theme.palette.paper,
                                    borderLeft: isCompleted ? '5px solid #01796f' : '5px solid darkred',
                                    transition: 'all 0.3s ease', // Smooth hover effect
                                    opacity: isCompleted ? 0.6 : 1,

                                    '&:hover': {
                                        borderLeft: isCompleted ? '5px solid #01796f' : '12px solid darkred',
                                        boxShadow: isCompleted ? 'none' : theme.shadows[8],
                                        transform: isCompleted ? 'none' : 'translateY(-2px)',
                                    },
                                    '& .MuiTypography-root': {
                                        color: (theme) => alpha(theme.palette.text.primary, 0.7),
                                    },
                                    '& .text-secondary': {
                                        color: 'text.secondary', // Lighter grey for emails/referrals
                                    }
                                })}
                            >
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        width: '100%' 
                                    }}
                                >
                                    {/* Candidate Info */}
                                    <Box 
                                        sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'flex-start', 
                                            width: '100%',
                                            height: '100%'
                                        }}
                                    >

                                        <Box sx={{width: '30%', height: '100%', alignSelf: "center"}}>

                                        <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold'}}>
                                            {candidate.name}
                                        </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                            Email: {candidate.email}
                                            </Typography>
                                            {candidate.referral && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Referral: {candidate.referral}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Box sx={{marginTop: '16px', width:'40%' }}>
                                            {isCompleted ? (
                                                <div>
                                                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '4px' }}>
                                                        Hiring Progress: {calculateCandidateProgress(candidate).toFixed(0)}%
                                                        <i> Completed!</i>
                                                    </Typography>
                                                </div>
                                            ) : (
                                                <div>
                                                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '4px' }}>
                                                        Hiring Progress: {calculateCandidateProgress(candidate).toFixed(0)}%
                                                    </Typography>
                                                </div>
                                            )}
                                            <LinearProgress
                                                variant="determinate"
                                                value={calculateCandidateProgress(candidate)}
                                                sx={{ 
                                                    height: 10,
                                                    width: '100%',
                                                    borderRadius: 5, 
                                                    backgroundColor: 'grey.300', '& .MuiLinearProgress-bar': { 
                                                        backgroundColor: 'darkred' 
                                                    }
                                                }}
                                            />

                                            <IconButton 
                                                aria-label="delete" 
                                                onClick={(event) => {
                                                    event.stopPropagation(); // Prevent Paper's onClick from firing
                                                    handleClickOpenDialog(candidate);
                                                }}
                                                className="delete-button" // Add a class for identifying the button
                                                sx={{ ml: 2, color: 'grey', '&:hover': { color: 'darkred' } }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                        <Box sx={{width:"10%", height:'100%'}}>
                                            <Typography variant="body2" color="text.secondary" sx={{}}>
                                                Clearance: {candidate.clearanceType !== "None" ? 'Yes' : 'No'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{}}>
                                                Polygraph: {candidate.polyType !== "None" ? 'Yes' : 'No'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                </Box>
                                
                            </Paper>
                        );
                    })}
                </Box>
            )}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog} // Allows closing by clicking outside or pressing Escape
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Removal"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {deleteStatus === 'idle' && `Are you sure you want to remove ${candidateToDelete?.name}?`}
                        {deleteStatus === 'deleting' && `Are you sure you want to remove ${candidateToDelete?.name}?`}
                        {deleteStatus === 'success' && "Success! Candidate has been removed."}
                        
                        {/*Error Message Display */}
                        {deleteStatus === 'error' && (
                            <span style={{ color: 'red', fontWeight: 'bold' }}>
                                Error: {deleteError}
                            </span>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    {deleteStatus === 'success' ? (
                            /* Only show ONE button when successful */
                            <Button 
                                onClick={handleCloseDialog} 
                                color="primary" 
                                variant="contained" 
                                autoFocus
                            >
                                Close
                            </Button>
                        ) : (
                            /* Show standard Cancel/Remove buttons otherwise */
                            <>
                                <Button onClick={handleCloseDialog} disabled={deleteStatus === 'deleting'}>
                                    Cancel
                                </Button>
                                <Button 
                                    onClick={handleConfirmDelete} 
                                    color="error" 
                                    disabled={deleteStatus === 'deleting' || deleteStatus === 'error'}
                                >
                                    {deleteStatus === 'deleting' ? "Removing..." : "Remove"}
                                </Button>
                            </>
                        )}                     
                </DialogActions>
            </Dialog>
        </div>
    );

}